import { memo, useCallback, useRef } from 'react';
import { Clock, AlertTriangle, User, UserCog, Users, Building2, UserCircle } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { cn } from '@/lib/utils';
import { PRIORITY_STYLES } from '@/lib/priority';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { SortedVisitItem } from './hooks/useSortedVisits';
import type { OwnershipType, ClientIndicators } from '@/api/visits';
import { tasksApi, type Task } from '@/api/tasks';
import { TaskCard } from '@/features/tasks/TaskCard';
import { ClientIndicatorBadges } from './components/ClientIndicatorBadges';
import { useAuth } from '@/auth';

interface VisitCardProps {
  item: SortedVisitItem;
  onItemClick?: (item: SortedVisitItem) => void;
  isActive?: boolean;
  /** Tasks for this client (passed from parent, fetched once for all clients) */
  clientTasks?: Task[];
  onTaskSelect?: (task: Task) => void;
  /** Client indicators (payment, delegation, docs) - loaded separately */
  indicators?: ClientIndicators;
  /** Client has items ready for waybill */
  hasWaybills?: boolean;
  /** Long-press callback for context menu */
  onLongPress?: (item: SortedVisitItem, pos: { x: number; y: number }) => void;
  /** Whether this card is highlighted (context menu open) */
  isHighlighted?: boolean;
}

// Day names in Russian (short)
const DAY_NAMES: Record<number, string> = {
  1: 'пн',
  2: 'вт',
  3: 'ср',
  4: 'чт',
  5: 'пт',
  6: 'сб',
  7: 'вс',
};

// Ownership icons (same as in OwnershipFilter)
const OWNERSHIP_ICONS: Record<OwnershipType, LucideIcon> = {
  own: User,
  additionalOffice: Building2,
  delegated: Users,
  subordinate: UserCog,
};

// Ownership labels for dropdown
const OWNERSHIP_LABELS: Record<OwnershipType, string> = {
  own: 'Мой',
  additionalOffice: 'Доп.офис',
  delegated: 'Делегирован',
  subordinate: 'Клиент сотрудника',
};

export const VisitCard = memo(function VisitCard({ item, onItemClick, isActive, clientTasks, onTaskSelect, indicators, hasWaybills, onLongPress, isHighlighted }: VisitCardProps) {
  const { client, preference, plannedForToday, deadlineStatus, visit, priority, ownershipTypes } = item;
  const style = PRIORITY_STYLES[priority];
  const queryClient = useQueryClient();
  const { user } = useAuth();

  // Показываем ответственного только если это не текущий пользователь
  // Для доп.офисов используем servicedByEmployee (кто обслуживает офис)
  // Для обычных клиентов используем responsibleEmployee из indicators
  const currentEmail = user?.email || null;
  const currentAbbr = currentEmail?.split('@')[0] || null;
  const respEmployee = item.isAdditionalOffice && item.servicedByEmployee
    ? { ...item.servicedByEmployee, abbr: null as string | null }
    : indicators?.responsibleEmployee ?? null;
  const showResponsible = respEmployee && respEmployee.abbr !== currentAbbr;

  // Автор визита (если не текущий пользователь)
  const visitEmployee = visit?.employee;
  const showVisitAuthor = visitEmployee && visitEmployee.email !== currentEmail;

  // Типы владения для отображения
  const displayTypes = ownershipTypes || [];
  const hasTypes = displayTypes.length > 0;

  // Tasks are now passed from parent (fetched once for all clients)
  const tasks = clientTasks || [];

  // Мутация для быстрого выполнения задачи
  const completeTask = useMutation({
    mutationFn: (taskId: string) => tasksApi.completeTask(taskId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });

  // Long-press handling
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const touchStart = useRef<{ x: number; y: number } | null>(null);
  const longPressFired = useRef(false);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (!onLongPress) return;
    const touch = e.touches[0];
    touchStart.current = { x: touch.clientX, y: touch.clientY };
    longPressFired.current = false;
    longPressTimer.current = setTimeout(() => {
      longPressFired.current = true;
      navigator.vibrate?.(50);
      onLongPress(item, { x: touch.clientX, y: touch.clientY });
    }, 200);
  }, [onLongPress, item]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!touchStart.current || !longPressTimer.current) return;
    const touch = e.touches[0];
    const dx = touch.clientX - touchStart.current.x;
    const dy = touch.clientY - touchStart.current.y;
    if (Math.abs(dx) > 10 || Math.abs(dy) > 10) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }, []);

  const handleCardClick = useCallback(() => {
    if (longPressFired.current) return;
    onItemClick?.(item);
  }, [onItemClick, item]);

  return (
    <div className="space-y-1">
    <div
      className={cn(
        'p-3 rounded-lg border transition-all active:scale-[0.98]',
        style.bg,
        isHighlighted
          ? 'border-dashed border-2 border-gray-400'
          : hasWaybills ? 'border-emerald-500 dark:border-emerald-600 border-2' : style.border,
        style.text,
        isActive && 'ring-2 ring-primary',
        onItemClick && 'cursor-pointer'
      )}
      onClick={handleCardClick}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onContextMenu={(e) => e.preventDefault()}
    >
      {/* Client name (or "Client (Office)" for additional offices) */}
      <div className="flex items-start justify-between gap-2">
        <h3
          className={cn(
            'font-medium text-sm leading-tight',
            style.text,
            style.strikethrough && 'line-through'
          )}
        >
          {item.displayName || client.name}
        </h3>

        {/* Status badges */}
        <div className="flex items-center gap-1 flex-shrink-0">
          {/* Client indicators (payment, delegation, frequency, docs) */}
          <ClientIndicatorBadges indicators={indicators} visitFrequencyWeeks={item.visitFrequencyWeeks} />
          {visit?.priority === 'URGENT' && priority !== 'been' && (
            <Badge variant="urgent" className="text-[10px] px-1.5">
              <AlertTriangle className="h-3 w-3 mr-0.5" />
              Срочно
            </Badge>
          )}
          {deadlineStatus === 'overdue' && (
            <Badge variant="deadline" className="text-[10px] px-1.5">
              Просрочено
            </Badge>
          )}
          {deadlineStatus === 'today' && (
            <Badge variant="today" className="text-[10px] px-1.5">
              Сегодня
            </Badge>
          )}
          {(deadlineStatus === 'today' || deadlineStatus === 'overdue') && visit?.plannedTime && (
            <Badge variant="outline" className="text-[10px] px-1.5 bg-white dark:bg-white/90 text-gray-800 dark:text-gray-800">
              {visit.plannedTime}
            </Badge>
          )}
          {deadlineStatus === 'tomorrow' && (
            <Badge variant="tomorrow" className="text-[10px] px-1.5">
              Завтра
            </Badge>
          )}
          {preference?.preferredDayOfWeek && (
            <Badge variant="outline" className="text-[10px] px-1.5 bg-white dark:bg-white/90 text-gray-800 dark:text-gray-800">
              {DAY_NAMES[preference.preferredDayOfWeek]}{preference.preferredTime ? ` ${preference.preferredTime}` : ''}
            </Badge>
          )}
          {hasTypes && (
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <Badge
                  variant="secondary"
                  className="px-1.5 cursor-pointer hover:bg-secondary/80"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex items-center gap-0.5">
                    {displayTypes.map((type: OwnershipType) => {
                      const Icon = OWNERSHIP_ICONS[type];
                      return <Icon key={type} className="h-3 w-3" />;
                    })}
                  </div>
                </Badge>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="min-w-[120px]"
                onClick={(e) => e.stopPropagation()}
              >
                {displayTypes.map((type: OwnershipType) => {
                  const Icon = OWNERSHIP_ICONS[type];
                  return (
                    <div key={type} className="flex items-center gap-2 px-2 py-1.5 text-sm">
                      <Icon className="h-4 w-4 text-muted-foreground" />
                      {OWNERSHIP_LABELS[type]}
                    </div>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      {/* Time info + Responsible employee + Visit author */}
      {(plannedForToday?.startTime || preference?.preferredTime || showResponsible || showVisitAuthor) && (
        <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
          {plannedForToday?.startTime && (
            <div className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              <span>{plannedForToday.startTime}</span>
            </div>
          )}

          {preference?.preferredTime && !plannedForToday?.startTime && !preference?.preferredDayOfWeek && (
            <div className="flex items-center gap-1 opacity-60">
              <Clock className="h-3 w-3" />
              <span>{preference.preferredTime}</span>
            </div>
          )}

          {/* Responsible employee (if not current user) */}
          {showResponsible && (
            <div className="flex items-center gap-1">
              <UserCircle className="h-3 w-3" />
              <span className="truncate max-w-[80px]">{respEmployee.lastName}</span>
            </div>
          )}

          {/* Visit author (if not current user) */}
          {showVisitAuthor && (
            <div className="flex items-center gap-1 text-green-600">
              <User className="h-3 w-3" />
              <span className="truncate max-w-[80px]">{visitEmployee.lastName}</span>
            </div>
          )}
        </div>
      )}
    </div>

      {/* Client tasks (OUTDOOR only) */}
      {tasks.length > 0 && (
        <div className="pl-4 space-y-1">
          {tasks.map((task) => (
            <TaskCard
              key={task.id}
              task={task}
              onComplete={(id) => completeTask.mutate(id)}
              onSelect={onTaskSelect}
              isCompleting={completeTask.isPending}
            />
          ))}
        </div>
      )}
    </div>
  );
});
